import React, { useRef } from "react";
import { polygon, walls } from "./cadGeometry";

const LABEL = { bath: "BATH", freestanding_bath: "F/S BATH", walkin_shower: "SHOWER", shower_tray: "TRAY", shower_screen: "SCREEN", shower_head: "RAIN HEAD", toilet: "WC", toilet_cc: "WC", toilet_wall: "WC", basin_counter: "BASIN", vanity: "VANITY", towel_rail: "RAIL", mirror: "MIRROR", mirror_rect: "MIRROR", niche: "NICHE", kitchen_unit: "UNITS", worktop: "WORKTOP" };

const Dim = ({ w }) => {
  const n = Math.hypot(w.nx, w.nz) || 1;
  const ox = (-w.nx / n) * 0.32, oz = (-w.nz / n) * 0.32;
  const a = [w.a[0] + ox, w.a[1] + oz], b = [w.b[0] + ox, w.b[1] + oz];
  let ang = (Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI;
  if (ang > 90 || ang < -90) ang += 180;
  const mx = (a[0] + b[0]) / 2, mz = (a[1] + b[1]) / 2;
  return (
    <g stroke="#8a8f98" strokeWidth={0.008}>
      <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} />
      <line x1={w.a[0]} y1={w.a[1]} x2={a[0]} y2={a[1]} strokeDasharray="0.03 0.03" />
      <line x1={w.b[0]} y1={w.b[1]} x2={b[0]} y2={b[1]} strokeDasharray="0.03 0.03" />
      <text x={mx} y={mz} transform={`rotate(${ang} ${mx} ${mz})`} dy={-0.04} fontSize={0.12} fill="#6b1e1a" stroke="none" textAnchor="middle" fontFamily="JetBrains Mono, monospace">{Math.round(w.len * 1000)}</text>
    </g>
  );
};

export const PlanView = ({ state, meta, selected, onSelect, onMove, onDragStart, selSurface, onSurface }) => {
  const svgRef = useRef(null);
  const drag = useRef(null);
  const { room } = state;
  const pad = 0.75;
  const pts = polygon(room);
  const ws = walls(room);
  const toLocal = (e) => {
    const p = svgRef.current.createSVGPoint();
    p.x = e.clientX; p.y = e.clientY;
    return p.matrixTransform(svgRef.current.getScreenCTM().inverse());
  };
  const snap = (v) => Math.round(v * 20) / 20;
  const grid = [];
  for (let x = 0; x <= room.length + 0.001; x += 0.1) grid.push(<line key={`x${x}`} x1={x} y1={0} x2={x} y2={room.width} stroke="#221f1c" strokeOpacity={Math.round(x * 10) % 5 === 0 ? 0.14 : 0.05} strokeWidth={0.005} />);
  for (let z = 0; z <= room.width + 0.001; z += 0.1) grid.push(<line key={`z${z}`} x1={0} y1={z} x2={room.length} y2={z} stroke="#221f1c" strokeOpacity={Math.round(z * 10) % 5 === 0 ? 0.14 : 0.05} strokeWidth={0.005} />);
  return (
    <svg ref={svgRef} viewBox={`${-pad} ${-pad} ${room.length + 2 * pad} ${room.width + 2 * pad}`} className="w-full h-full bg-[#f4efe6] touch-none select-none" data-testid="cad-plan-view"
      onPointerMove={(e) => { if (drag.current) { const p = toLocal(e); onMove(drag.current.uid, snap(p.x + drag.current.dx), snap(p.y + drag.current.dz)); } }}
      onPointerUp={() => { drag.current = null; }} onPointerDown={() => onSelect(null)}>
      <defs><clipPath id="roomclip"><polygon points={pts.map((p) => p.join(",")).join(" ")} /></clipPath></defs>
      <polygon points={pts.map((p) => p.join(",")).join(" ")} fill="#ebe4d8" />
      <g clipPath="url(#roomclip)">{grid}</g>
      {ws.map((w) => (
        <line key={w.key} x1={w.a[0]} y1={w.a[1]} x2={w.b[0]} y2={w.b[1]} stroke={selSurface === w.key ? "#b5552f" : "#221f1c"} strokeWidth={selSurface === w.key ? 0.1 : 0.06} strokeLinecap="square" className="cursor-pointer"
          onPointerDown={(e) => { e.stopPropagation(); onSurface(w.key); }} data-testid={`plan-wall-${w.key}`} />
      ))}
      {ws.map((w) => <Dim key={`d${w.key}`} w={w} />)}
      {state.fixtures.map((f) => {
        const m = meta[f.type];
        if (!m) return null;
        const sel = selected === f.uid;
        return (
          <g key={f.uid} transform={`translate(${f.x} ${f.z}) rotate(${-f.rot})`} className="cursor-move" data-testid={`plan-fixture-${f.type}`}
            onPointerDown={(e) => { e.stopPropagation(); const p = toLocal(e); onSelect(f.uid); onDragStart?.(); drag.current = { uid: f.uid, dx: f.x - p.x, dz: f.z - p.y }; try { svgRef.current.setPointerCapture(e.pointerId); } catch (err) { /* noop */ } }}>
            <rect x={-m.w / 2} y={-m.d / 2} width={m.w} height={Math.max(m.d, 0.04)} fill={sel ? "rgba(107,30,26,0.18)" : "rgba(246,241,232,0.9)"} stroke={sel ? "#6b1e1a" : "#221f1c"} strokeWidth={0.015} />
            <line x1={-m.w / 2} y1={m.d / 2} x2={m.w / 2} y2={m.d / 2} stroke="#6b1e1a" strokeWidth={0.02} />
            <text y={0.03} fontSize={Math.min(0.1, m.w / 5)} fill="#221f1c" textAnchor="middle" fontFamily="JetBrains Mono, monospace" pointerEvents="none">{LABEL[m.model] || "ITEM"}</text>
          </g>
        );
      })}
      <text x={room.length + pad - 0.1} y={room.width + pad - 0.32} textAnchor="end" fontSize={0.09} fill="#8a8f98" fontFamily="JetBrains Mono, monospace">PLAN · mm · 50mm snap</text>
    </svg>
  );
};

export default PlanView;
